import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Clock, Users, Loader2, CheckCircle2, XCircle, AlertCircle, RefreshCw, Zap } from 'lucide-react';

interface QueueStatusCardProps {
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'service_unavailable';
  progress: number;
  queuePosition?: number;
  estimatedTimeRemaining?: string | null;
  statusMessage?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  retryCount?: number;
  className?: string;
}

export const QueueStatusCard: React.FC<QueueStatusCardProps> = ({
  status,
  progress,
  queuePosition,
  estimatedTimeRemaining,
  statusMessage,
  priority,
  retryCount,
  className = ''
}) => {
  /**
   * Get status icon and color based on job status
   */
  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
      case 'queued':
        return <Clock className="h-5 w-5 text-yellow-500 animate-pulse" />;
      case 'processing':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <AlertCircle className="h-5 w-5 text-gray-500" />;
      case 'service_unavailable':
        return <RefreshCw className="h-5 w-5 text-orange-500 animate-pulse" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  /**
   * Get status badge color
   */
  const getStatusBadgeVariant = (): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'pending':
      case 'queued':
        return 'secondary';
      case 'processing':
        return 'default';
      case 'completed':
        return 'outline';
      case 'failed':
      case 'cancelled':
        return 'destructive';
      case 'service_unavailable':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  /**
   * Get priority badge info
   */
  const getPriorityBadge = () => {
    if (!priority || priority === 'normal') return null;

    const priorityConfig = {
      urgent: { label: 'Urgent', color: 'bg-red-500 text-white', icon: Zap },
      high: { label: 'Priority', color: 'bg-purple-500 text-white', icon: Zap },
      low: { label: 'Low Priority', color: 'bg-gray-400 text-white', icon: null },
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig];
    if (!config) return null;

    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        {Icon && <Icon className="h-3 w-3" />}
        {config.label}
      </Badge>
    );
  };

  /**
   * Get progress bar color
   */
  const getProgressBarColor = () => {
    switch (status) {
      case 'processing':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'failed':
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-yellow-500';
    }
  };

  /**
   * Should show the card (hide when completed after 3 seconds)
   */
  const shouldShowCard = status !== 'completed' && status !== 'failed' && status !== 'cancelled';

  if (!shouldShowCard) {
    return null;
  }

  return (
    <Card className={`border-2 ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {getStatusIcon()}
            <CardTitle className="text-lg">
              {status === 'queued' || status === 'pending'
                ? 'In Queue'
                : status === 'service_unavailable'
                ? 'Queued for Retry'
                : 'Processing'}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {getPriorityBadge()}
            <Badge variant={getStatusBadgeVariant()}>
              {status === 'service_unavailable'
                ? `Retry ${retryCount || 0}/5`
                : status.charAt(0).toUpperCase() + status.slice(1)}
            </Badge>
          </div>
        </div>
        {statusMessage && (
          <CardDescription className="mt-1">{statusMessage}</CardDescription>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{Math.round(progress * 100)}%</span>
          </div>
          <Progress
            value={progress * 100}
            className="h-2"
          />
        </div>

        {/* Queue Position */}
        {queuePosition !== undefined && queuePosition > 0 && (
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
            <Users className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">Position in Queue</p>
              <p className="text-xs text-muted-foreground">
                {queuePosition === 1
                  ? "You're next!"
                  : `${queuePosition - 1} ${queuePosition === 2 ? 'person' : 'people'} ahead of you`
                }
              </p>
            </div>
            <Badge variant="secondary" className="text-lg font-bold">
              #{queuePosition}
            </Badge>
          </div>
        )}

        {/* Estimated Time Remaining */}
        {estimatedTimeRemaining && (
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">Estimated Wait Time</p>
              <p className="text-xs text-muted-foreground">
                Your session will be ready soon
              </p>
            </div>
            <span className="text-sm font-semibold text-primary">
              {estimatedTimeRemaining}
            </span>
          </div>
        )}

        {/* Processing stages indicator */}
        {status === 'processing' && (
          <div className="pt-2 border-t">
            <p className="text-xs text-muted-foreground mb-2">Current Stage:</p>
            <div className="flex items-center gap-2">
              {progress < 0.3 && (
                <span className="text-xs flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  Generating script...
                </span>
              )}
              {progress >= 0.3 && progress < 0.7 && (
                <span className="text-xs flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  Converting to audio...
                </span>
              )}
              {progress >= 0.7 && progress < 1.0 && (
                <span className="text-xs flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  Finalizing session...
                </span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default QueueStatusCard;
